import { useRef, useEffect, useCallback, useState } from 'react';

/**
 * A custom hook that throttles the execution of a function
 * @param value The value to throttle
 * @param delay The throttle delay in milliseconds
 * @returns The throttled value
 */
export function useThrottle<T>(value: T, delay: number): T {
  const [throttledValue, setThrottledValue] = useState<T>(value);
  const lastExecuted = useRef<number>(Date.now());

  useEffect(() => {
    const now = Date.now();
    const remaining = delay - (now - lastExecuted.current);
    
    if (remaining <= 0) {
      lastExecuted.current = now;
      setThrottledValue(value);
    } else {
      const timerId = setTimeout(() => {
        lastExecuted.current = Date.now();
        setThrottledValue(value);
      }, remaining);
      
      return () => clearTimeout(timerId);
    }
  }, [value, delay]);

  return throttledValue;
}

/**
 * A custom hook that creates a throttled function
 * @param callback The function to throttle
 * @param delay The throttle delay in milliseconds
 * @returns A throttled version of the callback function
 */
export function useThrottleFunction<T extends (...args: any[]) => any>(
  callback: T,
  delay: number
): (...args: Parameters<T>) => void {
  const lastExecuted = useRef<number>(Date.now());
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastArgsRef = useRef<Parameters<T> | null>(null);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return useCallback(
    (...args: Parameters<T>) => {
      const now = Date.now();
      const remaining = delay - (now - lastExecuted.current);
      lastArgsRef.current = args;

      if (remaining <= 0) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        lastExecuted.current = now;
        callback(...args);
      } else if (!timeoutRef.current) {
        timeoutRef.current = setTimeout(() => {
          lastExecuted.current = Date.now();
          timeoutRef.current = null;
          if (lastArgsRef.current) {
            callback(...lastArgsRef.current);
          }
        }, remaining);
      }
    },
    [callback, delay]
  );
}

// Usage example
/*
import { useState } from 'react';
import { useThrottle, useThrottleFunction } from './useThrottle';

function ThrottleExample() {
  // Example 1: Throttle a value
  const [inputValue, setInputValue] = useState<string>('');
  const throttledValue = useThrottle<string>(inputValue, 500);

  // Example 2: Throttle a function
  const handleClick = useThrottleFunction(() => {
    console.log('Button clicked (throttled)');
    // API call or expensive operation here
  }, 1000);

  return (
    <div>
      <h2>Throttle Value Example</h2>
      <input
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        placeholder="Type something..."
      />
      <p>Input Value: {inputValue}</p>
      <p>Throttled Value: {throttledValue}</p>

      <h2>Throttle Function Example</h2>
      <button onClick={handleClick}>
        Click me (throttled)
      </button>
    </div>
  );
}
*/